import { z } from 'zod'
import { dueMomentMs, dueStatus } from '@/shared/lib'
import { TaskTypeSchema } from './schema'

// Задача глазами заказчика (00-architecture § 6). Состав полей — отдельный view
// бэкенда, а не обрезанный TaskView: внутреннего здесь нет вовсе — ни оценок и
// времени, ни исполнителей и переносов, ни тредов. Контекст, наоборот, разложен
// именами (проект/доска/колонка): клиент не знает наших идентификаторов.
export const CustomerTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  type: TaskTypeSchema,
  // Важность едет наружу: ради неё сотрудник задачу и открывает — «чем мы сейчас
  // заняты в первую очередь». Оценки и сложность — нет, это внутренняя кухня.
  priority: z.number().int().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  boardId: z.string().nullable(),
  boardName: z.string().nullable(),
  columnId: z.string().nullable(),
  columnName: z.string().nullable(),
  // Порядок колонки на доске — по нему собирается витринная доска. Нет — в конец.
  columnPosition: z.number().int(),
  // Состояние этапа словами витрины: над задачей идёт работа / работа ждёт ответа
  // клиента / работа закрыта. Сами флаги колонки (счёт времени) наружу не едут.
  inWork: z.boolean(),
  waiting: z.boolean(),
  done: z.boolean(),
  // Календарный день срока `YYYY-MM-DD` — без времени и без зоны.
  dueDate: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Когда задачу открыли клиенту — единственная часть аудита, которая ему видна
  // («с какого момента я это вижу»); кто открыл — внутреннее.
  customerVisibleAt: z.string().nullable(),
})

export type CustomerTask = z.infer<typeof CustomerTaskSchema>

export interface CustomerTaskDto {
  id: string
  title: string
  description?: string
  type?: string
  priority?: number
  project_id?: string
  project_name?: string
  board_id?: string
  board_name?: string
  column_id?: string
  column_name?: string
  column_position?: number
  in_work?: boolean
  waiting?: boolean
  done?: boolean
  due_date?: string
  created_at?: string
  updated_at?: string
  customer_visible_at?: string
}

export function toCustomerTask(dto: CustomerTaskDto): CustomerTask {
  const createdAt = dto.created_at ?? new Date(0).toISOString()
  return CustomerTaskSchema.parse({
    id: dto.id,
    title: dto.title,
    description: dto.description ?? '',
    type: dto.type ?? 'task',
    priority: dto.priority ?? null,
    projectId: dto.project_id ? dto.project_id : null,
    projectName: dto.project_name ? dto.project_name : null,
    boardId: dto.board_id ? dto.board_id : null,
    boardName: dto.board_name ? dto.board_name : null,
    columnId: dto.column_id ? dto.column_id : null,
    columnName: dto.column_name ? dto.column_name : null,
    columnPosition: dto.column_position ?? Number.MAX_SAFE_INTEGER,
    inWork: dto.in_work ?? false,
    waiting: dto.waiting ?? false,
    done: dto.done ?? false,
    dueDate: dto.due_date ?? null,
    createdAt,
    updatedAt: dto.updated_at ?? createdAt,
    customerVisibleAt: dto.customer_visible_at ?? null,
  })
}

// Табы витрины — теми же словами, что личная очередь сотрудника: клиент и команда
// спрашивают про задачу одинаково. Дефолт `all` живёт вне URL.
export type CustomerTaskFilter = 'all' | 'in-work' | 'waiting' | 'overdue' | 'done'

export function filterCustomerTasks(
  tasks: CustomerTask[],
  filter: CustomerTaskFilter,
  now?: Date,
): CustomerTask[] {
  return tasks.filter((task) => {
    if (filter === 'done') return task.done
    // Выполненные живут только на своей вкладке: витрина — про то, что идёт.
    if (filter === 'all') return !task.done
    if (filter === 'in-work') return task.inWork
    if (filter === 'waiting') return task.waiting
    // Выполненная не просрочена, чем бы ни кончился её срок: работа закрыта.
    return !task.done && task.dueDate != null && dueStatus(task.dueDate, now) === 'overdue'
  })
}

// Секции по срочности — тот же обход, что у команды, минус «Сейчас в работе»:
// таймеров заказчик не видит, и задача под таймером стоит у него по своему сроку.
export type CustomerSectionId = 'todayOverdue' | 'later' | 'noDue' | 'waiting' | 'done'

export interface CustomerSection {
  id: CustomerSectionId
  title: string
  tasks: CustomerTask[]
}

const SECTION_ORDER: { id: CustomerSectionId; title: string }[] = [
  { id: 'todayOverdue', title: 'Сегодня / Просрочено' },
  { id: 'later', title: 'Позже' },
  { id: 'noDue', title: 'Без срока' },
  { id: 'waiting', title: 'Ждёт ответа' },
  { id: 'done', title: 'Выполненные' },
]

// Приоритет: закрытая работа → «выполненные» (срок её оттуда не вытаскивает);
// ждёт ответа → своя секция; иначе по дедлайну.
function sectionOf(task: CustomerTask, now?: Date): CustomerSectionId {
  if (task.done) return 'done'
  if (task.waiting) return 'waiting'
  if (!task.dueDate) return 'noDue'
  const status = dueStatus(task.dueDate, now)
  return status === 'overdue' || status === 'today' ? 'todayOverdue' : 'later'
}

// Внутри секции — ближайший срок вверх, без срока в конец; тай-брейк по id, чтобы
// порядок не плавал между рендерами.
function byDue(a: CustomerTask, b: CustomerTask): number {
  const da = dueMomentMs(a.dueDate)
  const db = dueMomentMs(b.dueDate)
  return da === db ? a.id.localeCompare(b.id) : da - db
}

export function customerSections(tasks: CustomerTask[], now?: Date): CustomerSection[] {
  const buckets = new Map<CustomerSectionId, CustomerTask[]>()
  for (const task of tasks) {
    const id = sectionOf(task, now)
    const bucket = buckets.get(id)
    if (bucket) bucket.push(task)
    else buckets.set(id, [task])
  }
  return SECTION_ORDER.map(({ id, title }) => ({
    id,
    title,
    tasks: [...(buckets.get(id) ?? [])].sort(byDue),
  })).filter((section) => section.tasks.length > 0)
}

// Проект витрины: подпись плюс размер того, что клиенту открыто. Ни архивности,
// ни порядка, ни компании — витрина строится вокруг открытого, а не вокруг
// устройства проекта.
export const CustomerProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  boardCount: z.number().int(),
  taskCount: z.number().int(),
})

export type CustomerProject = z.infer<typeof CustomerProjectSchema>

export interface CustomerProjectDto {
  id: string
  name: string
  description?: string
  board_count?: number
  task_count?: number
}

export function toCustomerProject(dto: CustomerProjectDto): CustomerProject {
  return CustomerProjectSchema.parse({
    id: dto.id,
    name: dto.name,
    description: dto.description ?? '',
    boardCount: dto.board_count ?? 0,
    taskCount: dto.task_count ?? 0,
  })
}

// Доска в списке досок проекта. Отделов здесь нет: как студия устроена внутри,
// заказчика не касается.
export const CustomerBoardSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  taskCount: z.number().int(),
})

export type CustomerBoardSummary = z.infer<typeof CustomerBoardSummarySchema>

export interface CustomerBoardSummaryDto {
  id: string
  name: string
  project_id?: string
  project_name?: string
  task_count?: number
}

export function toCustomerBoardSummary(dto: CustomerBoardSummaryDto): CustomerBoardSummary {
  return CustomerBoardSummarySchema.parse({
    id: dto.id,
    name: dto.name,
    projectId: dto.project_id ?? '',
    projectName: dto.project_name ?? '',
    taskCount: dto.task_count ?? 0,
  })
}

// Колонка канбана заказчика: counts_time и waiting — внутренняя механика студии,
// наружу едет только done («работа закрыта»).
export const CustomerBoardColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number().int(),
  done: z.boolean(),
})

export type CustomerBoardColumn = z.infer<typeof CustomerBoardColumnSchema>

export const CustomerBoardSchema = z.object({
  id: z.string(),
  name: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  // Все колонки доски, включая пустые: иначе этапы у клиента разошлись бы с теми,
  // по которым работает команда.
  columns: z.array(CustomerBoardColumnSchema),
  tasks: z.array(CustomerTaskSchema),
})

export type CustomerBoard = z.infer<typeof CustomerBoardSchema>

export interface CustomerBoardDto {
  id: string
  name: string
  project_id?: string
  project_name?: string
  columns?: { id: string; name: string; position?: number; done?: boolean }[]
  tasks?: CustomerTaskDto[]
}

export function toCustomerBoard(dto: CustomerBoardDto): CustomerBoard {
  return {
    ...CustomerBoardSchema.omit({ columns: true, tasks: true }).parse({
      id: dto.id,
      name: dto.name,
      projectId: dto.project_id ?? '',
      projectName: dto.project_name ?? '',
    }),
    columns: (dto.columns ?? [])
      .map((c) =>
        CustomerBoardColumnSchema.parse({
          id: c.id,
          name: c.name,
          position: c.position ?? 0,
          done: c.done ?? false,
        }),
      )
      .sort((a, b) => a.position - b.position),
    tasks: (dto.tasks ?? []).map(toCustomerTask),
  }
}

export interface CustomerBoardLane {
  column: CustomerBoardColumn
  tasks: CustomerTask[]
}

// Раскладка канбана заказчика: колонки доски как есть (пустые остаются — этап без
// открытых задач всё равно этап), задачи в них — в порядке ответа бэка.
export function customerBoardLanes(board: CustomerBoard): CustomerBoardLane[] {
  const lanes = board.columns.map((column) => ({ column, tasks: [] as CustomerTask[] }))
  const byId = new Map(lanes.map((lane) => [lane.column.id, lane]))
  for (const task of board.tasks) {
    if (task.columnId) byId.get(task.columnId)?.tasks.push(task)
  }
  return lanes
}

// Колонка витринной доски: помеченные задачи, сгруппированные так же, как их
// видит команда, — иначе «доска» клиента разошлась бы с рабочей.
export interface CustomerColumn {
  id: string
  name: string
  tasks: CustomerTask[]
}

export function customerColumns(tasks: CustomerTask[]): CustomerColumn[] {
  const byColumn = new Map<string, { position: number; column: CustomerColumn }>()
  for (const task of tasks) {
    const id = task.columnId ?? 'unassigned'
    const entry = byColumn.get(id) ?? {
      position: task.columnPosition,
      column: { id, name: task.columnName ?? 'Без колонки', tasks: [] },
    }
    entry.column.tasks.push(task)
    byColumn.set(id, entry)
  }
  return [...byColumn.values()]
    .sort((a, b) => a.position - b.position || a.column.name.localeCompare(b.column.name))
    .map((entry) => entry.column)
}
